import React, { useState } from 'react';
import { Link } from 'gatsby';
import styled from 'styled-components';
import { ToastContainer, toast } from 'react-toastify';
import JobPostModal from './job-post-modal';
import { FacebookShareButton, TwitterShareButton, LinkedinShareButton } from 'react-share';
import Section from '../elements/section';
import Tooltip from '../elements/tooltip';
import Linkedin from '../icons/linkedIn';
import Twitter from '../icons/twitter';
import Facebook from '../icons/facebook';
import YellowBackArrowIcon from '../icons/yellow-back-arrow';
import CopyLinkIcon from '../icons/copyLink';
import media from '@styles/media';
import theme from '@styles/theme';

const { colors, titleSizes, fonts } = theme;

const CareerDetails = ({ data, location }) => {
  const [modalIsOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const [tooltipOpen, setTooltipOpen] = useState(false);

  const toggle = () => setTooltipOpen(!tooltipOpen);

  const {
    jobOrderId,
    title,
    publicDescription,
    publishedCategory,
    address,
    employmentType,
    dateLastPublished,
  } = data;

  const notify = () =>
    toast.success('You will be contacted by a recruiter shortly. Thank you!', {
      position: 'top-center',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });

  const handleOpenModal = () => {
    setIsOpen(true);
  };

  const handleCloseModal = () => {
    setIsOpen(false);
  };

  const sharingData = {
    jobOrderId,
    title,
    publishedCategory,
    employmentType,
    shareUrl: location && location.href,
  };

  const sharedAction = () => {
    // copiedNotify();
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 2000);

    navigator.clipboard.writeText(`${JSON.stringify(sharingData)}`);
  };

  const shareUrl = location && location.href;

  return (
    <Section padding="ph120">
      <StyledLayout>
        <StyledJobSideBar>
          <StyledHeader to={`/careers/`}>
            <YellowBackArrowIcon />
            Back to Job Search
          </StyledHeader>
          <StyledRightSideContent>
            <StyledJobTitle>{title}</StyledJobTitle>
            <StyledItem>
              <StyledItemTitle>Date Posted: &nbsp;</StyledItemTitle>
              {new Date(dateLastPublished).toLocaleDateString('en-US')}
            </StyledItem>
            <StyledItem>
              <StyledItemTitle>Job ID: &nbsp;</StyledItemTitle>
              Job #{jobOrderId}
            </StyledItem>
            <StyledItem>
              <StyledItemTitle>Employment Type: </StyledItemTitle>
              {employmentType}
            </StyledItem>
            <StyledItem>
              <StyledItemTitle>Location: </StyledItemTitle>
              {address.city},&nbsp;
              {address.state}
            </StyledItem>
            <StyledButton onClick={handleOpenModal}>Submit Resume</StyledButton>

            <StyledShareText>SHARE THIS JOB POST</StyledShareText>
            <StyledShareButtonsWrapp>
              <LinkedinShareButton url={shareUrl}>
                <Linkedin />
              </LinkedinShareButton>
              <FacebookShareButton url={shareUrl} quote={title}>
                <Facebook />
              </FacebookShareButton>
              <TwitterShareButton url={shareUrl} title={title}>
                <Twitter />
              </TwitterShareButton>
              <div
                role="button"
                tabIndex={0}
                className="copyButton"
                id="copy-button"
                onClick={() => sharedAction()}
                onKeyDown={() => sharedAction()}>
                <CopyLinkIcon />
              </div>
              <Tooltip
                placement={'top'}
                isOpen={tooltipOpen || copied}
                target="copy-button"
                toggle={toggle}>
                {copied ? 'Blog copied!' : 'Copy blog'}
              </Tooltip>
            </StyledShareButtonsWrapp>
          </StyledRightSideContent>
        </StyledJobSideBar>

        <StyledContents>
          <StyledDescription dangerouslySetInnerHTML={{ __html: publicDescription }} />
          <ToastContainer />
          <StyledLink>
            <a href="/careers/" aria-label="careers">
              Go to Careers
            </a>
            <a href="/" aria-label="Go to Home">
              Home
            </a>
          </StyledLink>
        </StyledContents>

        <JobPostModal
          isOpen={modalIsOpen}
          handleCloseModal={handleCloseModal}
          data={data}
          handleNotification={notify}
        />
      </StyledLayout>
    </Section>
  );
};

export default CareerDetails;

const StyledLayout = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  grid-column-gap: 32px;
  ${media.bigDesktop`display: block;`};

  .Toastify__toast-container--top-center {
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    min-width: 410px;
    .Toastify__toast--success {
      font-family: Montserrat;
      font-size: 16px;
      background: ${colors.lightPurple};
    }
    .Toastify__progress-bar--success {
      background: linear-gradient(90deg, #5215fc 0%, #ffc801 100%);
    }
  }
  .Toastify__toast-container {
    z-index: 999999;
  }
  .Toastify__toast--default {
    background: #f2f2f2;
    color: black;
    font-size: 14px;
  }
  .Toastify__progress-bar--default {
    background: linear-gradient(90deg, #5215fc 0%, #ffc801 100%);
  }
`;

const StyledJobSideBar = styled.div`
  background: linear-gradient(180deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%), #ffffff;
  box-shadow: 0px 4px 28px rgba(0, 0, 0, 0.15);
  position: sticky;
  top: 130px;
  padding-bottom: 24px;
  height: fit-content;
  grid-column: 1 / 5;
  min-width: 455px;
  ${media.giant`grid-column: 1 / 5; min-width: auto;`};
  ${media.bigDesktop`width: 100%; position: relative; top: 0px; padding-top: 16px;`};
`;

const StyledHeader = styled(Link)`
  position: relative;
  width: calc(100% - 50px);
  ${titleSizes.h6_medium};
  color: ${colors.white};
  margin: 32px 50px 0 0;
  padding: 32px 64px 27px 64px;
  overflow: hidden;
  display: flex;
  align-items: center;
  svg {
    width: 35px;
    height: 16px;
  }
  cursor: pointer;
  transition-duration: 0.25s;
  &:hover {
    svg {
      transform: translateX(-4px);
      transition-duration: 0.25s;
    }
  }

  ${media.bigGiant`
    width: calc(100% - 10px);
    margin: 24px 10px 0 0;
    padding: 30px 32px;
  `};
  ${media.giant`
    width: calc(100% - 5px);
    margin-bottom: 0;
    padding: 30px 20px 30px 15px;
  `};

  ${media.bigDesktop`z-index: 1;`};
  ${media.phablet`font-size: 18px;`};
  &:after {
    content: '';
    position: absolute;
    width: 130%;
    height: 100%;
    right: 20px;
    top: 0;
    z-index: -1;
    background-color: ${colors.darkPurple};
    transform: skew(-13deg);
    ${media.bigDesktop`
      max-width: 100%;
      transform: skew(-13deg) translateX(-80px);
    `};
    ${media.phablet`transform: skew(-13deg) translateX(-20px);`};
  }
`;

const StyledRightSideContent = styled.div`
  padding: 64px 32px 64px 64px;
  ${media.bigGiant`padding: 40px 10px 40px 40px;`};
  ${media.giant`padding: 40px 8px 40px 24px;`};
  ${media.bigDesktop`padding: 40px 20px 40px 24px;`};
`;

const StyledJobTitle = styled.div`
  padding-bottom: 48px;
  font-style: normal;
  font-weight: 900;
  font-size: 42px;
  line-height: 133%;
  text-transform: uppercase;
  ${media.bigGiant`font-size: 24px;`};
`;

const StyledItem = styled.div`
  font-style: normal;
  font-weight: normal;
  font-size: 24px;
  line-height: 1.33;
  padding-bottom: 32px;
  ${media.bigGiant`font-size: 18px; padding-bottom: 24px;`};
  ${media.giant`padding-bottom: 12px;`};
  ${media.phablet`font-size: 16px;`};
`;

const StyledItemTitle = styled.span`
  font-weight: bold;
`;

const StyledContents = styled.div`
  grid-column: 6 / -1;
  ${media.giant`grid-column: 5 / -1;`};
  ${media.bigDesktop`width: 100%;`};
`;

const StyledDescription = styled.div`
  margin-top: 32px;
  margin-bottom: 42px;
  display: block;
  h2,
  div,
  p,
  span,
  ul,
  strong,
  li {
    font-family: ${fonts.Montserrat} !important;
    font-style: normal !important;
    font-weight: normal !important;
    font-size: 28px !important;
    line-height: 170% !important;
    text-align: left !important;
    margin-block-end: 0em;
    margin-block-start: 0em;
    letter-spacing: normal;
    ${media.bigGiant`font-size: 20px !important;`};
    ${media.bigDesktop`font-size: 20px !important;`};
    ${media.phone`font-size: 16px !important;`};
  }
  p {
    padding: 3px 0px 8px 0px;
  }
  strong {
    font-weight: bold !important;
  }
  ul {
    margin-block-start: 16px;
    margin-block-end: 16px;
  }
  li {
    font-size: 28px !important;
    ${media.bigGiant`font-size: 20px !important;`};
    ${media.bigDesktop`font-size: 20px !important;`};
    ${media.phone`font-size: 16px !important;`};
  }
  p {
    padding: 3px 0px 8px 0px;
  }
`;

const StyledButton = styled.div`
  margin-top: 32px;
  padding: 24px 56px;
  background-color: ${colors.lightPurple};
  text-transform: uppercase;
  color: white;
  font-style: normal;
  font-weight: bold;
  font-size: 24px;
  line-height: 133%;
  width: fit-content;
  transition: all 0.2s ease-in;
  cursor: pointer;
  &:hover {
    background-color: ${colors.darkPurple};
  }
  ${media.bigGiant`font-size: 18px; padding: 14px 43px; margin-top: 20px;`};
`;

const StyledShareText = styled.div`
  margin-top: 88px;
  font-style: normal;
  font-weight: bold;
  font-size: 24px;
  line-height: 133%;
  ${media.bigGiant`font-size: 18px; margin-top: 72px;`};
`;

const StyledShareButtonsWrapp = styled.div`
  margin-top: 24px;
  color: white;
  font-size: 18px;
  font-weight: bold;
  min-width: fit-content;
  display: flex;
  align-items: center;

  button {
    margin-right: 16px;
    cursor: pointer;
    svg path {
      stroke: white;
      fill: #000;
    }
  }
  .copyButton {
    margin-left: 8px;
    width: 25px;
    height: 25px;
    display: inline-block;
    cursor: pointer;

    svg path {
      stroke: black;
    }
  }
`;

const StyledLink = styled.div`
  display: none;
  z-index: -1;
  position: absolute;
  width: 0;
  height: 0;
`;
